import { useEffect } from 'react';
import { usePrevious } from 'react-use';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import type { RootState } from '../app/store';

// Custom hook for bidirectional URL ⟷ Redux sync
export function useUrlReduxBidirectionalSync<T>({
  urlValue,
  updateUrl,
  reduxSelector,
  reduxAction,
  defaultValue,
}: {
  // URL parameter value (from usePageParams)
  urlValue: T | null | undefined;
  // Function to update URL parameters
  updateUrl: (value: T) => void;
  // Redux selector to get current Redux value
  reduxSelector: (state: RootState) => T | null | undefined;
  // Redux action creator to update Redux state
  reduxAction: (value: T) => { type: string; payload: T };
  // Default value when both URL and Redux are empty
  defaultValue?: T;
}) {
  const dispatch = useAppDispatch();
  const reduxValue = useAppSelector(reduxSelector);

  // Track previous values to determine source of change
  const previousUrlValue = usePrevious(urlValue);
  const previousReduxValue = usePrevious(reduxValue);

  useEffect(() => {
    const hasUrlValue =
      urlValue !== null && urlValue !== undefined && urlValue !== '';
    const hasReduxValue =
      reduxValue !== null && reduxValue !== undefined && reduxValue !== '';

    // Determine if values changed from their previous state
    const urlChanged = urlValue !== previousUrlValue;
    const reduxChanged = reduxValue !== previousReduxValue;

    // Initial render (both values change from undefined) or both changed simultaneously
    if (
      (previousUrlValue === undefined && previousReduxValue === undefined) ||
      (urlChanged && reduxChanged)
    ) {
      if (hasUrlValue && urlValue !== reduxValue) {
        // Prioritize URL to preserve deep-linking functionality
        dispatch(reduxAction(urlValue as T));
      } else if (hasReduxValue && !hasUrlValue) {
        // Redux has value but URL empty → sync to URL
        updateUrl(reduxValue as T);
      } else if (!hasUrlValue && !hasReduxValue && defaultValue !== undefined) {
        // Both empty → set default to Redux only (URL will be set by usePageParams)
        dispatch(reduxAction(defaultValue));
      }
      return;
    }

    // URL changed but Redux did not
    if (urlChanged && !reduxChanged) {
      if (hasUrlValue && urlValue !== reduxValue) {
        // URL changed → sync to Redux
        dispatch(reduxAction(urlValue as T));
      } else if (!hasUrlValue && hasReduxValue) {
        // URL was cleared but Redux has value → sync to URL
        updateUrl(reduxValue as T);
      }
      return;
    }

    // Redux changed but URL did not
    if (reduxChanged && !urlChanged) {
      if (hasReduxValue && reduxValue !== urlValue) {
        // Redux changed → sync to URL
        updateUrl(reduxValue as T);
      } else if (!hasReduxValue && hasUrlValue) {
        // Redux was cleared but URL has value → sync to Redux
        dispatch(reduxAction(urlValue as T));
      }
      return;
    }
  }, [
    urlValue,
    reduxValue,
    previousUrlValue,
    previousReduxValue,
    defaultValue,
    dispatch,
    reduxAction,
    updateUrl,
  ]);
}
